import { EQpBadgeColor, EQpBadgeSize, EQpBadgeType } from '@library/components/qp-badge/qp-badge.models';
import { EQpDividerOrientation } from '@library/components/qp-divider/qp-divider.models';
import { QpDropdownNavComponent } from '@library/components/qp-dropdown-nav/qp-dropdown-nav.component';
import { EQpProfile } from '@library/models/qp-profile.models';
import { IQpDisplayedRoute } from '@library/models/qp-router.models';
import { QpResponsiveService } from '@library/services/qp-responsive/qp-responsive.service';
import { QuickSearchComponent } from '@one/app/components/subnav/quick-search/quick-search.component';
import { SubnavBreadcrumbsModule } from '@one/app/components/subnav-breadcrumbs/subnav-breadcrumbs.module';
import { Account } from '@one/app/shared/classes/accounts/account';
import { LeaderFieldModeButtonComponent } from '@one/app/shared/components/leader-field-mode-button/leader-field-mode-button.component';
import { IAccount } from '@one/app/shared/models/account/account.models';
import { EMenuPosition } from '@one/app/shared/models/menu/menu.models';
import { AccountService } from '@one/app/shared/services/account/account.service';
import { LeaderFieldModeService } from '@one/app/shared/services/auth/leader-field-mode.service';
import { DynamicSubnavRoutingStore } from '@one/app/shared/services/dynamic-subnav-routing-store/dynamic-subnav-routing-store.service';
import { DisplayedRoutesService } from '@one/app/shared/services/router/displayed-routes.service';
import { CommonModule } from '@angular/common';
import { AfterViewChecked, ChangeDetectorRef, Component, inject, OnInit } from '@angular/core';
import { NavigationEnd, Router, RouterModule, IsActiveMatchOptions } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateModule } from '@ngx-translate/core';
import { EQimaButtonSize, EQimaButtonStyle, EQimaIconName, QimaButtonModule, QimaOptionalType } from '@qima/ngx-qima';
import { isEmpty, isFunction, isNil } from 'lodash/index';
import { Observable, of, switchMap } from 'rxjs';
import { distinctUntilChanged, map, take, tap } from 'rxjs/operators';

@UntilDestroy()
@Component({
  selector: 'one-subnav',
  standalone: true,
  imports: [
    CommonModule,
    QimaButtonModule,
    RouterModule,
    TranslateModule,
    QuickSearchComponent,
    SubnavBreadcrumbsModule,
    QpDropdownNavComponent,
    LeaderFieldModeButtonComponent,
  ],
  templateUrl: './subnav.component.html',
  styleUrls: ['./subnav.component.scss'],
})
export class SubnavComponent implements OnInit, AfterViewChecked {
  public currentMenuPosition = EMenuPosition.COLLAPSE;
  public readonly menuPositions = EMenuPosition;
  public readonly dividerOrientations: typeof EQpDividerOrientation = EQpDividerOrientation;
  public subMenu: QimaOptionalType<IQpDisplayedRoute> = undefined;
  public subRoutesDisplayed = false;
  public isInspector = false;
  public isBrand = false;
  public isSupervisor = false;
  public creationRoutes: IQpDisplayedRoute[] = [];
  public actionMenu: IQpDisplayedRoute[] = [];
  public shouldDisplayQuickSearch = false;
  public showLeaderFieldModeToolbar = false;
  public readonly badgeSizes = EQpBadgeSize;
  public readonly badgeTypes = EQpBadgeType;
  public readonly badgeColors = EQpBadgeColor;
  public readonly qimaButtonSizes: typeof EQimaButtonSize = EQimaButtonSize;
  public readonly qimaButtonStyles: typeof EQimaButtonStyle = EQimaButtonStyle;
  public readonly iconNames: typeof EQimaIconName = EQimaIconName;

  public readonly _displayedRoutesService = inject(DisplayedRoutesService);
  public readonly _router = inject(Router);
  public readonly _dynamicSubnavRoutingStore = inject(DynamicSubnavRoutingStore);
  public readonly _changeDetectorRef = inject(ChangeDetectorRef);
  public readonly _accountService = inject(AccountService);
  public readonly _qpResponsiveService = inject(QpResponsiveService);
  private readonly _leaderFieldModeService = inject(LeaderFieldModeService);

  private readonly LEAF_PAGE_REGEX = /(\d|details)$/;

  public ngAfterViewChecked(): void {
    this._accountService
      .getAccount$()
      .pipe(untilDestroyed(this))
      .subscribe((iAccount: QimaOptionalType<IAccount>): void => {
        if (iAccount) {
          const account = Account.create(iAccount);

          this._setUserRoles(account);

          this.shouldDisplayQuickSearch = account.hasBrandRole() || account.hasSupervisorRole();
          this._changeDetectorRef.detectChanges();
        }
      });
  }

  public ngOnInit(): void {
    this._leaderFieldModeService
      .canShowFieldMode$()
      .pipe(untilDestroyed(this))
      .subscribe((canShow): void => {
        this.showLeaderFieldModeToolbar = canShow;
        this._changeDetectorRef.detectChanges();
      });

    this._displayedRoutesService.menuPosition$.pipe(untilDestroyed(this)).subscribe({
      next: (menuPosition: EMenuPosition): void => {
        this.currentMenuPosition = menuPosition;
      },
    });
    this.calculateRoutes();
    // We need to re-check the displayed routes on route change
    this._router.events.pipe(untilDestroyed(this)).subscribe({
      next: (event): void => {
        if (event instanceof NavigationEnd) {
          this.calculateRoutes();
        }
      },
    });
    this._dynamicSubnavRoutingStore.routes$
      .pipe(
        switchMap((routes: IQpDisplayedRoute): Observable<QimaOptionalType<IQpDisplayedRoute>> => {
          if (this._dynamicSubnavRoutingStore.isCustomRoute(routes)) {
            return this._getRoutes$().pipe(
              tap({
                next: (): void => {
                  const saveCreationButtonLabel = this.subMenu ? this.subMenu.creationButtonLabel : '';

                  this.subMenu = routes;
                  this._calculateActions();
                  this._updateActions();
                  this.subRoutesDisplayed = this.shouldDisplaySubRoutes();
                  this.subMenu.creationButtonLabel = saveCreationButtonLabel;
                  this._changeDetectorRef.detectChanges();
                },
              })
            );
          }

          return of(null);
        }),
        untilDestroyed(this)
      )
      .subscribe();
    this._accountService
      .getAccount$()
      .pipe(distinctUntilChanged(), untilDestroyed(this))
      .subscribe((account: QimaOptionalType<IAccount>): void => {
        if (account) {
          this._setUserRoles(Account.create(account));
        }
      });
  }

  public calculateRoutes(): void {
    this._getRoutes$()
      .pipe(take(1), untilDestroyed(this))
      .subscribe((subMenu): void => {
        if (isNil(subMenu) && !this.isInspector) {
          this.subMenu = null;
          this._changeDetectorRef.detectChanges();

          return;
        }

        this.subMenu = subMenu;

        if (subMenu?.children) {
          this._calculateActions();
          this._updateActions();
          this.creationRoutes = this.actionMenu;
          this._displayedRoutesService.isSubmenuActive$.next(true);
        } else {
          this._displayedRoutesService.isSubmenuActive$.next(false);
        }

        this.subRoutesDisplayed = this.shouldDisplaySubRoutes();
        this._changeDetectorRef.detectChanges();
      });
  }

  public shouldDisplaySubRoutes(): boolean {
    if (this.subMenu) {
      if (this._accountService.isInspector()) {
        this._displayedRoutesService.setInspectorInspectionsStatistics$().pipe(untilDestroyed(this)).subscribe();
      }

      if (this.LEAF_PAGE_REGEX.test(this._router.url.split('?')[0])) {
        return false;
      }

      if (this.subMenu.children && !this.subMenu.isSubMenuHidden) {
        const activatedSubRoute = this.subMenu.children.find((route): boolean =>
          this.isDisplayedRouteActive(route.routerLink, route.routerLinkActiveOption.exact)
        );

        return activatedSubRoute !== undefined;
      }
    }

    return false;
  }

  public get isNotMobile(): boolean {
    return !this._qpResponsiveService.isMobile();
  }

  /**
   * Collects `isDefaultAction` routes under this menu branch. By default, merges actions from every
   * first-level tab (e.g. SP product-po). When `route.aggregateSiblingDefaultActions === false`, only
   * the active first-level tab is considered (e.g. SP reports: "Create one" only on report groups).
   * @param route
   * @param actionRoutes
   */
  public getAllChildrenActions(route: QimaOptionalType<IQpDisplayedRoute>, actionRoutes: IQpDisplayedRoute[]): IQpDisplayedRoute[] {
    if (route) {
      if (route.children && !isEmpty(route.children)) {
        route.children.forEach((childRoute: IQpDisplayedRoute): void => {
          if (route.aggregateSiblingDefaultActions === false) {
            const exact = childRoute.routerLinkActiveOption?.exact ?? false;

            if (!this.isDisplayedRouteActive(childRoute.routerLink, exact)) {
              return;
            }
          }

          actionRoutes = this.getAllChildrenActions(childRoute, actionRoutes);
        });
      } else if (route.isDefaultAction) {
        actionRoutes.push(route);
      }
    }

    return actionRoutes;
  }

  public onSubNavLinkClick(): void {
    if (this.subMenu && isFunction(this.subMenu.creationButtonClickCallback)) {
      this.subMenu.creationButtonClickCallback();

      return;
    }

    const activeTab = this.subMenu?.children?.find((child: IQpDisplayedRoute): boolean =>
      this.isDisplayedRouteActive(child.routerLink, child.routerLinkActiveOption?.exact ?? false)
    );

    if (activeTab && isFunction(activeTab.creationButtonClickCallback)) {
      activeTab.creationButtonClickCallback();
    }
  }

  /** Single default action: callbacks, then router navigation when the route is link-based. */
  public onSingleDefaultCreationActionClick(): void {
    const route = this.subMenu?.defaultActionRoutes?.[0];

    this.onSubNavLinkClick();

    if (route?.isRouterLinkDisabled !== true && route?.routerLink) {
      void this._router.navigateByUrl(route.routerLink);
    }
  }

  public onSubRouteClick(subEntity: IQpDisplayedRoute): void {
    if (subEntity.isCounterDisplayed) {
      subEntity.subNavButtonClickCallback?.().pipe(untilDestroyed(this)).subscribe();
    }
  }

  public linkActiveMatchOptions(exact: boolean): IsActiveMatchOptions {
    return {
      paths: exact ? 'exact' : 'subset',
      queryParams: 'ignored',
      fragment: 'ignored',
      matrixParams: 'ignored',
    };
  }

  public isDisplayedRouteActive(routerLink: string, exact = false): boolean {
    return this._router.isActive(routerLink, this.linkActiveMatchOptions(exact));
  }

  private _calculateActions(): void {
    this.actionMenu = this.getAllChildrenActions(this.subMenu, []);
  }

  private _updateActions(): void {
    if (this.subMenu) {
      this.subMenu.defaultActionRoutes = this.actionMenu;
      this.subMenu.isDefaultAction = this.actionMenu.length > 0 ? true : this.subMenu.isDefaultAction;
    }
  }

  private _getRoutes$(): Observable<QimaOptionalType<IQpDisplayedRoute>> {
    return this._displayedRoutesService.getRoutes().pipe(
      switchMap(
        (routes: IQpDisplayedRoute[]): Observable<IQpDisplayedRoute[]> =>
          this._displayedRoutesService
            .getMiddleRoutes()
            .pipe(map((middleRoutes: IQpDisplayedRoute[]): IQpDisplayedRoute[] => [...routes, ...middleRoutes]))
      ),
      map((routes: IQpDisplayedRoute[]): QimaOptionalType<IQpDisplayedRoute> => {
        if (!isEmpty(routes)) {
          return routes.find((route: IQpDisplayedRoute): boolean => {
            return this.isDisplayedRouteActive(route.routerLink, route.routerLinkActiveOption.exact);
          });
        }

        return null;
      })
    );
  }

  private _setUserRoles(account: Account): void {
    if (account?.profiles) {
      this.isInspector = account.profiles.some((accountProfile: EQpProfile): boolean => {
        return [EQpProfile.ROLE_INSPECTOR, EQpProfile.ROLE_SERVICE_PROVIDER_INSPECTOR].includes(accountProfile);
      });
      this.isSupervisor = account.profiles.includes(EQpProfile.ROLE_SUPERVISOR);
      this.isBrand = account.profiles.includes(EQpProfile.ROLE_BRAND);
    } else {
      this.isInspector = false;
      this.isSupervisor = false;
      this.isBrand = false;
    }
  }
}
